import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { API_URL } from "@/shared/config";
import { useAuth } from "@/app/providers";
import { apiService } from "@/shared/api";
import { SIDEBAR_API } from "@/constants/api.sidebar";
import { EMPTY_SIDEBAR, isPathAllowed } from "@/shared/sidebar/pathAllowed";
import type { SidebarConfig } from "@/shared/sidebar/types";

type SidebarNavContextValue = {
  loading: boolean;
  error: string | null;
  config: SidebarConfig;
  canAccessPath: (path: string) => boolean;
  refresh: () => Promise<void>;
};

const SidebarNavContext = createContext<SidebarNavContextValue | null>(null);

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, { config: SidebarConfig; ts: number }>();

function cacheKey(entreprisePk: number | null | undefined, buildingPk: number | null) {
  return `${entreprisePk ?? 0}:${buildingPk ?? 0}`;
}

export function SidebarNavProvider({ children }: { children: ReactNode }) {
  const { token, user, currentBuilding } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<SidebarConfig>(EMPTY_SIDEBAR);

  const entreprisePk =
    user && typeof (user as any).entreprisePk === "number"
      ? (user as any).entreprisePk
      : null;
  const buildingPk = currentBuilding?.pk ? Number(currentBuilding.pk) : null;

  const fetchSidebar = useCallback(async () => {
    if (!token) {
      setConfig(EMPTY_SIDEBAR);
      setLoading(false);
      return;
    }

    const key = cacheKey(entreprisePk, buildingPk);
    const cached = cache.get(key);
    const now = Date.now();
    if (cached && now - cached.ts < CACHE_TTL_MS) {
      setConfig(cached.config);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const url = `${API_URL}${SIDEBAR_API.MY}${
        buildingPk ? `?building_pk=${buildingPk}` : ""
      }`;
      const response: any = await apiService.get(url, token);
      const fields = response?.fields || response;
      const next: SidebarConfig = {
        entreprisePk: Number(fields?.entreprisePk) || 0,
        updatedAt: fields?.updatedAt ?? null,
        items: Array.isArray(fields?.items) ? fields.items : [],
        allowedPaths: Array.isArray(fields?.allowedPaths)
          ? fields.allowedPaths
          : EMPTY_SIDEBAR.allowedPaths,
      };
      cache.set(key, { config: next, ts: Date.now() });
      setConfig(next);
    } catch (e: any) {
      setError(e?.message || "Failed to load sidebar");
      setConfig(EMPTY_SIDEBAR);
    } finally {
      setLoading(false);
    }
  }, [token, entreprisePk, buildingPk]);

  useEffect(() => {
    void fetchSidebar();
  }, [fetchSidebar]);

  const canAccessPath = useCallback(
    (path: string) => isPathAllowed(config.allowedPaths, path),
    [config.allowedPaths],
  );

  const value = useMemo(
    () => ({
      loading,
      error,
      config,
      canAccessPath,
      refresh: fetchSidebar,
    }),
    [loading, error, config, canAccessPath, fetchSidebar],
  );

  return (
    <SidebarNavContext.Provider value={value}>
      {children}
    </SidebarNavContext.Provider>
  );
}

export function useSidebarNav(): SidebarNavContextValue {
  const ctx = useContext(SidebarNavContext);
  if (!ctx) {
    throw new Error("useSidebarNav must be used within SidebarNavProvider");
  }
  return ctx;
}
